import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import globalPhoneCodeJSON from './global-phone-codes.json';

interface GlobalPhoneCodeEntry {
  region: string;
  code: string;
  regionAbbr: string;
}

type GlobalPhoneCodeRecord = GlobalPhoneCodeEntry[];
type GlobalPhoneOption = { label: string; value: string, code: string };
const globalPhoneCodeRecord = globalPhoneCodeJSON as GlobalPhoneCodeRecord;

const US_REGION_ABBR = 'US' as const;
const EXCLUDED_REGIONS = ['AZ', 'LK', 'BD', 'OM', 'IR', 'PK'];

const useGlobalPhoneCodes = () => {
  const { t } = useTranslation();
  const globalPhoneCodeOptions = useMemo(() => Object.keys(globalPhoneCodeRecord).reduce((accum, key) => {
    const entry = globalPhoneCodeRecord[key as keyof typeof globalPhoneCodeRecord] as GlobalPhoneCodeEntry;
    if (entry && !EXCLUDED_REGIONS.includes(entry.regionAbbr)) {
      const { region, regionAbbr, code } = entry;
      const option = { label: t('globalPhoneCodes.labels.option', { code, region }), value: regionAbbr, code   };
      if (option.value === US_REGION_ABBR) {
        // sort w/ US phone code as the first option
        accum.unshift(option);
      } else {
        accum.push(option);
      }
    }
    return accum;
  }, [] as GlobalPhoneOption[]), [t]);

  const defaultOption = useMemo(() => globalPhoneCodeOptions.find((option) => option.value === US_REGION_ABBR) as GlobalPhoneOption, [globalPhoneCodeOptions]);

  const getRenderedOption = (value: string | number | undefined) => {
    const matchingOption = globalPhoneCodeOptions.find((option) => option.value === value);
    return matchingOption ? t('globalPhoneCodes.labels.renderedOption', { code: matchingOption.code, regionAbbr: matchingOption.value }) : '';
  };

  const getCodeFromRegionAbbr = (regionAbbr?: string) => {
    const matchingOption = globalPhoneCodeOptions.find((option) => option.value === regionAbbr);
    return matchingOption ? matchingOption.code : '';
  };

  return {
    options: globalPhoneCodeOptions,
    defaultOption,
    getRenderedOption,
    getCodeFromRegionAbbr,
  };

};

export { useGlobalPhoneCodes };
